import React from 'react';
import { RADIUS, TAP, useTheme, type Theme } from '../../theme';

type FieldCtx = { id: string; describedBy?: string; invalid: boolean };
const Ctx = React.createContext<FieldCtx | null>(null);

/** Etikett, hjälptext och felmeddelande runt ett Input/Select/Textarea. */
export function Field({ label, hint, error, children, style }: {
  label: React.ReactNode; hint?: React.ReactNode; error?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties;
}) {
  const T = useTheme();
  const id = React.useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined;
  return (
    <Ctx.Provider value={{ id, describedBy, invalid: !!error }}>
      {/* minWidth 0: fältet får krympa i flex/grid i stället för att trycka ut ur raden. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...style }}>
        <label htmlFor={id} style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{label}</label>
        {children}
        {hint && <div id={hintId} style={{ fontSize: 13, color: T.textMuted }}>{hint}</div>}
        {error && <div id={errId} role="alert" style={{ fontSize: 13, fontWeight: 600, color: T.danger }}>{error}</div>}
      </div>
    </Ctx.Provider>
  );
}

function fieldStyle(T: Theme, invalid: boolean): React.CSSProperties {
  return {
    width: '100%', minWidth: 0, maxWidth: '100%', minHeight: TAP, padding: '10px 14px', borderRadius: RADIUS.md, fontSize: 16, fontFamily: 'inherit',
    background: T.surface, color: T.text, border: `1px solid ${invalid ? T.danger : T.borderHover}`,
  };
}

function useFieldProps(id?: string) {
  const ctx = React.useContext(Ctx);
  return { id: id ?? ctx?.id, 'aria-describedby': ctx?.describedBy, 'aria-invalid': ctx?.invalid || undefined, invalid: !!ctx?.invalid };
}

const DATE_TYPES = new Set(['date', 'time', 'datetime-local', 'month', 'week']);

/**
 * iOS Safari ger datum- och tidsfält en egen minsta bredd som struntar i width: 100%,
 * så de sticker ut ur smala rader. Utan inbyggt utseende följer de bredden (väljaren öppnas ändå).
 */
const dateInputStyle: React.CSSProperties = { display: 'block', WebkitAppearance: 'none', appearance: 'none' };

/** Textfält, 44 px högt och 16 px text (zoomar inte i iOS). */
export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ style, id, ...rest }, ref) {
  const T = useTheme();
  const { invalid, ...a } = useFieldProps(id);
  const date = rest.type ? DATE_TYPES.has(rest.type) : false;
  return <input ref={ref} {...a} {...rest} style={{ ...fieldStyle(T, invalid), ...(date ? dateInputStyle : null), ...style }} />;
});

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function Select({ style, id, ...rest }, ref) {
  const T = useTheme();
  const { invalid, ...a } = useFieldProps(id);
  return <select ref={ref} {...a} {...rest} style={{ ...fieldStyle(T, invalid), ...style }} />;
});

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ style, id, ...rest }, ref) {
  const T = useTheme();
  const { invalid, ...a } = useFieldProps(id);
  return <textarea ref={ref} rows={4} {...a} {...rest} style={{ ...fieldStyle(T, invalid), lineHeight: 1.5, resize: 'vertical', ...style }} />;
});
